import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '@/components/feature/AuthContext';
import QrPlaceholder from '@/components/base/QrPlaceholder';
import { getSignPayState, signActivity } from '@/services/activity';
import { formatSignFee } from '@/services/adapters';

const CAREER_OPTIONS = ['医师', '药师', '护理', '医学生', '科研人员', '企业', '其他'];
const EDUCATION_OPTIONS = ['专科', '本科', '硕士', '博士', '其他'];
const INTENT_OPTIONS = ['拜师师承', '跟诊学习', '科研合作'];

/** 扫码支付步骤提示（与课程购买弹窗一致） */
const PAY_STEPS = [
  { id: 1, icon: 'ri-qr-scan-2-line', label: '微信扫一扫' },
  { id: 2, icon: 'ri-apps-2-line', label: '进入小程序' },
  { id: 3, icon: 'ri-shield-check-line', label: '完成支付' },
];

/** 支付状态（与后端 SignPayState 对齐） */
const PAY_STATE_PAID = 2;

/** 支付状态轮询间隔（毫秒） */
const PAY_POLL_INTERVAL = 3000;

interface TrainingEnrollFormProps {
  /** 培训课题名称，随报名备注一并提交，便于后台区分报名来源 */
  topic: string;
  /** 活动 ID（提交到 /activity/userSignActivity） */
  activityId: number;
  /** 活动报名费（元，0 表示免费） */
  signFee: number;
  /** 待支付报名单 ID（详情页"继续支付"传入，打开后直接进入扫码支付步骤） */
  initialSignId?: number;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error' | 'pay';

export default function TrainingEnrollForm({
  topic,
  activityId,
  signFee,
  initialSignId = 0,
}: TrainingEnrollFormProps) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');
  /** 手动刷新支付状态时的提示信息 */
  const [payHint, setPayHint] = useState('');
  /** 完成支付的金额（成功文案展示用） */
  const [paidAmount, setPaidAmount] = useState(0);
  /** 本次流程是否以"已支付"收尾（区分成功文案） */
  const [paidDone, setPaidDone] = useState(false);

  const { requireLogin } = useAuth();
  /** 报名单 ID（报名提交/继续支付后回填，下单、轮询、重试使用） */
  const signIdRef = useRef(initialSignId);
  /** 防止"继续支付"入口在 StrictMode 下重复建单 */
  const bootRef = useRef(false);

  /** 进入扫码支付步骤（报名支付统一在「元合 VITA」小程序内完成，官网仅提供扫码入口） */
  const startPay = async () => {
    if (!signIdRef.current) return;
    setPayHint('');
    setStatus('pay');

    // 先同步一次最新支付状态：已完成支付则直接进入成功状态
    try {
      const state = await getSignPayState(signIdRef.current);
      if (state.payState === PAY_STATE_PAID) {
        setPaidAmount(state.signFee || signFee);
        setPaidDone(true);
        setStatus('success');
      }
    } catch {
      // 状态同步失败不阻塞扫码引导：支付结果由轮询与手动刷新兜底
    }
  };

  /** 扫码支付完成后手动刷新支付状态（轮询之外的兜底入口） */
  const refreshPayState = async () => {
    if (!signIdRef.current) return;
    try {
      const state = await getSignPayState(signIdRef.current);
      if (state.payState === PAY_STATE_PAID) {
        setPaidAmount(state.signFee || signFee);
        setPaidDone(true);
        setStatus('success');
        return;
      }
      setPayHint('暂未检测到支付结果，请在小程序内完成支付后再点击刷新。');
    } catch (err) {
      setPayHint(err instanceof Error ? err.message : '支付状态查询失败，请稍后重试。');
    }
  };

  /** 支付状态轮询：用户在小程序内完成支付后自动切换为报名成功 */
  useEffect(() => {
    if (status !== 'pay') return undefined;

    let cancelled = false;
    let inFlight = false;
    const timer = window.setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const state = await getSignPayState(signIdRef.current);
        if (!cancelled && state.payState === PAY_STATE_PAID) {
          window.clearInterval(timer);
          setPaidAmount(state.signFee || signFee);
          setPaidDone(true);
          setStatus('success');
        }
      } catch {
        // 轮询失败静默处理，等待下一轮重试
      } finally {
        inFlight = false;
      }
    }, PAY_POLL_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [status, signFee]);

  /** 详情页"继续支付"入口：打开弹窗后直接进入扫码支付步骤 */
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    if (initialSignId > 0) void startPay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 将表单内容整理为报名备注（随报名记录提交，后台活动报名列表可见） */
  const buildRemark = (data: FormData): string => {
    const parts: string[] = [];
    const intents: string[] = [];
    data.forEach((value, key) => {
      if (key === 'mobile_alt' || key === '培训课题') return;
      const text = String(value).trim();
      if (!text) return;
      if (key === '申请意向') {
        intents.push(text);
        return;
      }
      parts.push(`${key}：${text}`);
    });
    if (intents.length) parts.push(`申请意向：${intents.join('、')}`);
    return [`培训课题：${topic}`, ...parts].join('\n');
  };

  const submitEnroll = async (formEl: HTMLFormElement) => {
    // 立即在异步操作前读取真实表单值（含浏览器自动填充）
    const data = new FormData(formEl);

    // 反垃圾蜜罐：被填写则静默视为成功，不发送任何请求
    const honey = String(data.get('mobile_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      formEl.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const result = await signActivity(activityId, buildRemark(data));
      formEl.reset();
      signIdRef.current = result.signId;

      // 重复报名且已完成支付：直接展示成功状态
      if (result.payState === PAY_STATE_PAID) {
        setPaidAmount(result.signFee || signFee);
        setPaidDone(true);
        setStatus('success');
        return;
      }

      // 收费活动：进入扫码支付步骤
      if (result.needPay) {
        void startPay();
        return;
      }

      // 免费报名完成
      setPaidDone(false);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error ? err.message : '提交失败，请稍后重试。');
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;

    if (status === 'submitting') return;

    // 未登录先弹登录，登录成功后自动继续提交
    requireLogin(() => {
      void submitEnroll(formEl);
    });
  };

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-lg border border-accent-200 bg-accent-50">
        <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h3 className="font-heading text-lg font-bold text-foreground-900">
          {paidDone ? '报名成功' : '报名信息已提交'}
        </h3>
        {paidDone && (
          <p className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-accent-100 text-accent-800 text-sm font-medium">
            <i className="ri-wechat-pay-line"></i>
            已支付 {formatSignFee(paidAmount)}
          </p>
        )}
        <p className="mt-2 text-sm text-foreground-600 max-w-md">
          {paidDone
            ? `支付成功，报名已确认。感谢报名《${topic}》，会务组将在 1-2 个工作日内与您电话确认，请保持手机畅通。`
            : `感谢报名《${topic}》。会务组将在 1-2 个工作日内与您电话确认，请保持手机畅通。`}
        </p>
        <button
          type="button"
          onClick={() => {
            setStatus('idle');
            setPaidDone(false);
            setPaidAmount(0);
          }}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          再报一场
        </button>
      </div>
    );
  }

  if (status === 'pay') {
    return (
      <div className="flex flex-col items-center text-center py-2">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-4 rounded-full bg-accent-500"></span>
          <span className="text-sm font-medium text-accent-700">微信扫码支付</span>
        </div>
        <p className="mt-3 text-xs text-foreground-500">报名费用</p>
        <p className="font-heading text-2xl font-bold text-foreground-900">{formatSignFee(signFee)}</p>

        <div className="mt-4 w-44 h-44 p-2 rounded-lg bg-background-50 border border-background-200">
          <QrPlaceholder />
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-sm text-foreground-700">
          <i className="ri-wechat-line text-accent-600"></i>
          请使用微信扫码，进入小程序完成支付
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-loader-4-line animate-spin"></i>
          支付完成后自动确认报名，请勿关闭弹窗
        </p>

        <div className="w-full grid grid-cols-3 gap-2 mt-4">
          {PAY_STEPS.map((step) => (
            <div key={step.id} className="flex flex-col items-center gap-2 rounded-md bg-background-100 py-3 px-1">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
                <i className={step.icon}></i>
              </span>
              <span className="text-xs text-foreground-600 whitespace-nowrap">{step.label}</span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => void refreshPayState()}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          刷新支付状态
        </button>

        {payHint && (
          <p className="mt-3 text-xs text-foreground-500 leading-relaxed max-w-xs">{payHint}</p>
        )}

        <p className="mt-3 text-xs leading-relaxed text-foreground-400">
          支付将在「元合 VITA」小程序内完成，官网仅提供扫码入口
        </p>
      </div>
    );
  }

  return (
    <form id="training-enroll-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="enroll-name" className={labelClass}>
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="enroll-name"
            name="姓名"
            required
            maxLength={30}
            placeholder="请输入真实姓名"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="enroll-phone" className={labelClass}>
            手机号 <span className="text-primary-500">*</span>
          </label>
          <input
            id="enroll-phone"
            name="手机号"
            type="tel"
            required
            maxLength={20}
            pattern="[0-9+\- ]{6,20}"
            placeholder="用于接收报名确认通知"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="enroll-career" className={labelClass}>
            职业
          </label>
          <select id="enroll-career" name="职业" defaultValue="" className={`${inputClass} cursor-pointer`}>
            <option value="" disabled>
              请选择职业
            </option>
            {CAREER_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="enroll-education" className={labelClass}>
            学历
          </label>
          <select id="enroll-education" name="学历" defaultValue="" className={`${inputClass} cursor-pointer`}>
            <option value="" disabled>
              请选择学历
            </option>
            {EDUCATION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="enroll-major" className={labelClass}>
          专业
        </label>
        <input
          id="enroll-major"
          name="专业"
          maxLength={40}
          placeholder="请输入所学 / 从事专业，如：中西医结合、针灸推拿"
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="enroll-org" className={labelClass}>
            单位
          </label>
          <input
            id="enroll-org"
            name="单位"
            maxLength={60}
            placeholder="请输入所在医院 / 机构全称"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="enroll-address" className={labelClass}>
            地址
          </label>
          <input
            id="enroll-address"
            name="地址"
            maxLength={80}
            placeholder="请输入所在城市 / 通讯地址"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <span className={labelClass}>申请意向（可多选）</span>
        <div className="flex flex-wrap gap-2.5">
          {INTENT_OPTIONS.map((option) => (
            <label
              key={option}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer hover:border-primary-300 has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50 transition-colors"
            >
              <input type="checkbox" name="申请意向" value={option} className="accent-primary-500 w-4 h-4" />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      {/* 反垃圾蜜罐字段 */}
      <input
        type="text"
        name="mobile_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot-aux"
      />

      {status === 'error' && formError && (
        <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            {signFee > 0 ? '报名并支付' : '确认报名'}
          </>
        )}
      </button>

      <p className="text-xs text-foreground-500 leading-relaxed text-center">
        提交即表示同意会务组使用以上信息与您取得联系，我们将严格保护您的个人隐私。
      </p>
    </form>
  );
}
